/**
 * Subtle, soothing UI sound effects — paired with [[haptic]] so taps feel
 * alive on every interactive surface. Per user directive 2026-05-18:
 * "ui and ux should feel alive with soothing feedback of haptic and
 * sound feedback".
 *
 * Design rules per [[feedback-dopamine-for-all]]:
 *
 * - **Soothing, not jingles.** Single soft tones with a smooth attack +
 *   gentle decay envelope. Never a multi-note melody — those become
 *   irritating after the third interaction. Think Linear's hover ping,
 *   not Windows XP startup.
 * - **Opt-in by default.** Web audio context can't initialise until first
 *   user gesture, AND we don't make noise without explicit consent.
 *   Caller persists the preference via `setSoundEnabled(boolean)`.
 *   `sound.tap()` / `success()` / `celebration()` are no-ops until both
 *   conditions are met.
 * - **Respect reduced motion + a future reduced-sound media query.**
 *   `prefers-reduced-motion: reduce` silences sound too — users who opt
 *   out of motion typically prefer a calmer overall experience.
 * - **Lazy AudioContext.** Constructed on first sound (after gesture),
 *   reused for subsequent calls. Suspended when idle would be nice but
 *   browsers handle that automatically.
 * - **Built on Web Audio API.** No `<audio>` tags. No bundled MP3s. No
 *   network requests. Just synthesised sine/triangle waves with an
 *   amplitude envelope — sub-kilobyte cost.
 */

const LS_KEY = 'kilat.soundEnabled';

let ctx: AudioContext | null = null;
let enabledCache: boolean | null = null;

function readEnabled(): boolean {
  if (enabledCache !== null) return enabledCache;
  if (typeof window === 'undefined') return false;
  try {
    const v = window.localStorage.getItem(LS_KEY);
    enabledCache = v === '1';
  } catch {
    enabledCache = false;
  }
  return enabledCache;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx) return ctx;
  const Ctor =
    window.AudioContext ??
    (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
    return ctx;
  } catch {
    return null;
  }
}

interface ToneSpec {
  /** Frequency in Hz. */
  readonly freq: number;
  /** Total duration including decay, in seconds. */
  readonly duration: number;
  /** Peak gain (0..1). Soothing = stay under 0.15. */
  readonly gain: number;
  /** Waveform — `sine` is calmest, `triangle` slightly brighter. */
  readonly type?: OscillatorType;
  /** Optional pitch slide ending frequency for a gentle glide. */
  readonly slideTo?: number;
}

function playTone(spec: ToneSpec): void {
  const ac = getContext();
  if (!ac) return;
  // Resume in case the browser auto-suspended after idle.
  if (ac.state === 'suspended') {
    void ac.resume().catch(() => undefined);
  }
  const now = ac.currentTime;
  const osc = ac.createOscillator();
  const gainNode = ac.createGain();
  osc.type = spec.type ?? 'sine';
  osc.frequency.setValueAtTime(spec.freq, now);
  if (typeof spec.slideTo === 'number') {
    osc.frequency.linearRampToValueAtTime(spec.slideTo, now + spec.duration);
  }
  // Amplitude envelope: gentle attack (10ms), exponential decay over the rest.
  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(spec.gain, now + 0.01);
  gainNode.gain.exponentialRampToValueAtTime(0.0001, now + spec.duration);
  osc.connect(gainNode).connect(ac.destination);
  osc.start(now);
  osc.stop(now + spec.duration + 0.05);
}

function guardedPlay(spec: ToneSpec | ToneSpec[]): void {
  if (!readEnabled()) return;
  if (prefersReducedMotion()) return;
  const list = Array.isArray(spec) ? spec : [spec];
  for (const t of list) playTone(t);
}

export const sound = {
  /** Soft tap — paired with `haptic.tap()` on primary CTA presses. */
  tap(): void {
    guardedPlay({ freq: 880, duration: 0.08, gain: 0.05, type: 'sine' });
  },
  /** Confirmation — pleasant rising glide for a save / status advance. */
  success(): void {
    guardedPlay({ freq: 660, slideTo: 990, duration: 0.18, gain: 0.06, type: 'sine' });
  },
  /** Celebration — a brief two-tone arpeggio, paired with confetti. */
  celebration(): void {
    guardedPlay([
      { freq: 660, duration: 0.12, gain: 0.07, type: 'triangle' },
      { freq: 990, duration: 0.18, gain: 0.07, type: 'triangle' },
    ]);
  },
};

/** Caller toggles user preference. Persists across sessions. */
export function setSoundEnabled(enabled: boolean): void {
  enabledCache = enabled;
  try {
    window.localStorage.setItem(LS_KEY, enabled ? '1' : '0');
  } catch {
    /* swallow */
  }
}

/** Caller reads current preference to render an opt-in toggle. */
export function isSoundEnabled(): boolean {
  return readEnabled();
}

// `feel()` is exported from `./index.ts` rather than here so this file
// stays free of cross-module imports. The composed call is one line,
// not worth the dynamic-import dance the prior version used.
